import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SaintForm from "../components/SaintForm";
import Toast from "../components/Toast";
import Button from "../components/ui/Button";
import { Card, CardBody, CardHeader } from "../components/ui/Card";
import { useToast } from "../hooks/useToast";
import { useAuth } from "../hooks/useAuth";
import {
  fetchPendingSaints,
  getSaint,
  saveSaint,
  renameSaint,
  deleteSaint,
} from "../services/saintsRepo";
import { slugify } from "../lib/slugify";

function toInitialValuesFromSaint(s) {
  const galleryUrls = Array.isArray(s?.gallery) ? s.gallery : [];
  const galleryCaps = Array.isArray(s?.galleryCaptions) ? s.galleryCaptions : [];

  const gallery = galleryUrls
    .map((url, i) => ({
      url,
      caption: String(galleryCaps?.[i] ?? ""),
    }))
    .filter((x) => x.url);

  return {
    id: s?.id || "",
    name: s?.name || "",
    stage: s?.stage || "Saint",

    countryLower: s?.countryLower || "",

    bio: s?.bio || "",
    isPope: !!s?.isPope,

    bornText: s?.bornText || "",
    diedText: s?.diedText || "",

    beatifiedISO: s?.beatifiedText || "",
    canonizedISO: s?.canonizedText || "",

    feastKeys: Array.isArray(s?.feastKeys) ? s.feastKeys : [],
    feastKey: s?.feastKey || "",
    feastMonth: s?.feastMonth || "",
    feastDay: s?.feastDay || "",

    categories: Array.isArray(s?.categories) ? s.categories : [],
    patronage: Array.isArray(s?.patronage) ? s.patronage : [],
    quotes: Array.isArray(s?.quotes) ? s.quotes : [],
    prayers: Array.isArray(s?.prayers) ? s.prayers : [],

    gallery,
    contributors: Array.isArray(s?.contributors) ? s.contributors : [],
  };
}

function EmptyState({ title, body, linkTo = "/search", linkLabel = "Back to search" }) {
  return (
    <div className="space-y-4">
      <section className="rounded-[30px] bg-dulia-hero p-6 text-white shadow-card">
        <div className="space-y-2">
          <div className="text-xs font-semibold uppercase tracking-[0.16em] text-dulia-goldSoft">
            Admin
          </div>
          <h1 className="text-3xl font-semibold tracking-tight md:text-4xl">
            Pending Requests
          </h1>
        </div>
      </section>

      <Card className="rounded-[28px]">
        <CardBody className="space-y-3">
          <div className="text-base font-semibold text-dulia-ink dark:text-dulia-parchment">
            {title}
          </div>
          <div className="text-sm text-dulia-navy/75 dark:text-dulia-parchment/72">
            {body}
          </div>
          <div>
            <Link
              to={linkTo}
              className="text-sm font-medium text-dulia-navy underline decoration-dulia-gold/55 underline-offset-4 hover:text-dulia-navyAlt dark:text-dulia-goldSoft dark:hover:text-dulia-parchment"
            >
              {linkLabel}
            </Link>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

function QueueItem({ item, active, busy, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={[
        "w-full rounded-2xl border px-4 py-3 text-left transition",
        active
          ? "border-dulia-lineStrong bg-dulia-parchment text-dulia-ink shadow-soft dark:border-dulia-gold/35 dark:bg-dulia-night dark:text-dulia-parchment"
          : "border-dulia-line bg-dulia-panel text-dulia-ink hover:bg-dulia-parchment dark:border-dulia-lineStrong/30 dark:bg-dulia-night dark:text-dulia-parchment dark:hover:bg-dulia-nightSoft",
        busy ? "cursor-not-allowed opacity-70" : "",
      ].join(" ")}
    >
      <div className="font-medium">{item.name || item.id}</div>
      <div className="mt-1 text-xs text-dulia-navy/70 dark:text-dulia-parchment/68">
        saints/{item.id}
      </div>
    </button>
  );
}

export default function AdminRequests() {
  const navigate = useNavigate();
  const { user, loading, admin, adminLoading } = useAuth();
  const { message: toast, show: showToast, clear: clearToast } = useToast(2500);

  const [pending, setPending] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [selectedSaint, setSelectedSaint] = useState(null);
  const [busy, setBusy] = useState(false);

  const [submitIntent, setSubmitIntent] = useState("save"); // "save" | "approve"
  const formWrapRef = useRef(null);

  const gateLoading = loading || adminLoading;

  async function refreshPending() {
    const list = await fetchPendingSaints();

    const sorted = list.sort((a, b) => {
      const aT = a?.updatedAt?.toMillis ? a.updatedAt.toMillis() : 0;
      const bT = b?.updatedAt?.toMillis ? b.updatedAt.toMillis() : 0;
      return bT - aT;
    });

    setPending(sorted);

    if (selectedId && !sorted.some((x) => x.id === selectedId)) {
      setSelectedId("");
      setSelectedSaint(null);
    }
  }

  useEffect(() => {
    if (gateLoading) return;
    if (!user || !admin) return;
    refreshPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateLoading, user, admin]);

  useEffect(() => {
    (async () => {
      if (!selectedId) return;
      const s = await getSaint(selectedId);
      setSelectedSaint(s);
    })();
  }, [selectedId]);

  const initialValues = useMemo(() => {
    if (!selectedSaint) return null;
    return toInitialValuesFromSaint(selectedSaint);
  }, [selectedSaint]);

  async function handleSave(payload, meta) {
    if (!selectedSaint?.id) return { ok: false, message: "No selection." };

    const approveNow = submitIntent === "approve";
    const approvedValue = approveNow ? true : false;

    const oldId = selectedSaint.id;
    const desiredId = String(meta?.docId || "").trim();
    const fallback = slugify(payload?.name);
    const nextId = desiredId || fallback || oldId;

    try {
      setBusy(true);

      if (nextId !== oldId) {
        const res = await renameSaint(
          oldId,
          nextId,
          { ...payload, approved: approvedValue },
          { existingRand: selectedSaint.rand }
        );

        if (!res?.ok) {
          showToast(res?.message || "Rename failed.");
          return { ok: false, message: res?.message || "Rename failed." };
        }

        showToast(
          approveNow
            ? `Approved: saints/${nextId}`
            : res.message || `Renamed: saints/${oldId} → saints/${nextId}`
        );

        await refreshPending();

        if (approveNow) {
          setSelectedId("");
          setSelectedSaint(null);
          navigate(`/profile/${nextId}`);
        } else {
          setSelectedId(nextId);
        }
      } else {
        const res = await saveSaint(
          oldId,
          { ...payload, approved: approvedValue },
          { existingRand: selectedSaint.rand }
        );

        showToast(
          approveNow ? `Approved: saints/${oldId}` : res?.message || `Saved: saints/${oldId}`
        );

        await refreshPending();

        if (approveNow) {
          setSelectedId("");
          setSelectedSaint(null);
          navigate(`/profile/${oldId}`);
        } else {
          const fresh = await getSaint(oldId);
          setSelectedSaint(fresh);
        }
      }

      return { ok: true, message: "Saved." };
    } catch (e) {
      return { ok: false, message: String(e?.message || e || "Save failed.") };
    } finally {
      setBusy(false);
      setSubmitIntent("save");
    }
  }

  async function denySelected() {
    if (!selectedSaint?.id) return;

    const ok = window.confirm(
      `Delete this pending request?\n\nsaints/${selectedSaint.id}\n\nThis cannot be undone.`
    );
    if (!ok) return;

    try {
      setBusy(true);
      await deleteSaint(selectedSaint.id);
      showToast(`Deleted: saints/${selectedSaint.id}`);
      await refreshPending();
      setSelectedId("");
      setSelectedSaint(null);
    } catch (e) {
      showToast(String(e?.message || e || "Delete failed."));
    } finally {
      setBusy(false);
    }
  }

  if (gateLoading) {
    return (
      <div className="space-y-4">
        <Toast message={toast} onClose={clearToast} />
        <section className="rounded-[30px] bg-dulia-hero p-6 text-white shadow-card">
          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-[0.16em] text-dulia-goldSoft">
              Admin
            </div>
            <h1 className="text-3xl font-semibold tracking-tight md:text-4xl">
              Pending Requests
            </h1>
          </div>
        </section>

        <Card className="rounded-[28px]">
          <CardBody className="text-sm text-dulia-navy/75 dark:text-dulia-parchment/75">
            Loading…
          </CardBody>
        </Card>
      </div>
    );
  }

  if (!user) {
    return (
      <EmptyState
        title="You must be signed in."
        body="Sign in with an authorized account to review pending saint profile requests."
      />
    );
  }

  if (!admin) {
    return (
      <EmptyState
        title="Not authorized."
        body="This page is only available to admins who can review, edit, approve, or deny pending requests."
      />
    );
  }

  return (
    <div className="space-y-5">
      <Toast message={toast} onClose={clearToast} />

      <section className="rounded-[30px] bg-dulia-hero p-6 text-white shadow-card">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-[0.16em] text-dulia-goldSoft">
              Requests
            </div>
            <h1 className="text-3xl font-semibold tracking-tight md:text-4xl">
              Pending Requests
            </h1>
            <p className="max-w-2xl text-sm leading-6 text-white/80 md:text-base">
              Review submitted profiles here. You can approve them as-is, approve them with changes, or deny them. Approved profiles will be published and visible to the public. Denied profiles will be deleted.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-dulia-goldSoft">
              {pending.length} request{pending.length === 1 ? "" : "s"}
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={refreshPending}
              disabled={busy}
            >
              Refresh
            </Button>
          </div>
        </div>
      </section>

      <div className="grid gap-5 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Card className="overflow-hidden rounded-[28px]">
            <CardHeader className="px-5 py-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-lg font-semibold text-dulia-ink dark:text-dulia-parchment">
                    Queue
                  </div>
                  <div className="mt-1 text-sm text-dulia-navy/75 dark:text-dulia-parchment/70">
                    Most recently updated first.
                  </div>
                </div>
                {busy ? (
                  <div className="text-xs font-medium text-dulia-gold dark:text-dulia-goldSoft">
                    Working…
                  </div>
                ) : null}
              </div>
            </CardHeader>

            <CardBody className="space-y-3">
              {pending.length === 0 ? (
                <div className="rounded-2xl border border-dulia-line bg-dulia-parchment/60 p-4 text-sm text-dulia-navy/80 dark:border-dulia-lineStrong/20 dark:bg-dulia-night dark:text-dulia-parchment/75">
                  No pending requests.
                </div>
              ) : (
                pending.map((p) => (
                  <QueueItem
                    key={p.id}
                    item={p}
                    active={p.id === selectedId}
                    busy={busy}
                    onClick={() => setSelectedId(p.id)}
                  />
                ))
              )}
            </CardBody>
          </Card>
        </div>

        <div className="space-y-4 lg:col-span-8">
          {!selectedSaint ? (
            <Card className="rounded-[28px]">
              <CardBody className="text-sm text-dulia-navy/80 dark:text-dulia-parchment/75">
                Select a request from the queue to review and edit it.
              </CardBody>
            </Card>
          ) : (
            <>
              <Card className="rounded-[28px]">
                <CardHeader className="px-5 py-4">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-lg font-semibold text-dulia-ink dark:text-dulia-parchment">
                        Reviewing: {selectedSaint.name || selectedSaint.id}
                      </div>
                      <div className="mt-1 text-sm text-dulia-navy/75 dark:text-dulia-parchment/70">
                        saints/{selectedSaint.id}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="primary"
                        onClick={() => {
                          setSubmitIntent("approve");
                          const formEl = formWrapRef.current?.querySelector("form");
                          if (formEl?.requestSubmit) formEl.requestSubmit();
                          else {
                            formEl?.dispatchEvent(
                              new Event("submit", {
                                bubbles: true,
                                cancelable: true,
                              })
                            );
                          }
                        }}
                        disabled={busy}
                        title="Marks approved=true"
                      >
                        Approve
                      </Button>

                      <Button
                        type="button"
                        variant="danger"
                        onClick={denySelected}
                        disabled={busy}
                        title="Deletes the request"
                      >
                        Deny
                      </Button>
                    </div>
                  </div>
                </CardHeader>

                <CardBody className="space-y-2">
                  <div className="rounded-2xl border border-dulia-line bg-dulia-parchment/55 px-4 py-3 text-sm text-dulia-navy/80 dark:border-dulia-lineStrong/20 dark:bg-dulia-night dark:text-dulia-parchment/75">
                    Use the form below to edit the request.{" "}
                    <span className="font-medium">Save</span> keeps it pending.{" "}
                    <span className="font-medium">Approve</span> saves it with approval
                    and opens the profile.
                  </div>
                </CardBody>
              </Card>

              <div ref={formWrapRef}>
                <SaintForm
                  mode="edit"
                  initialValues={initialValues}
                  onSubmit={handleSave}
                  submitLabel={busy ? "Saving…" : "Save (keep pending)"}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
